// Sidebar and TopNav removed

import {
  Box,
  Button,
  Chip,
  Grid,
  Stack,
  Typography,
  Card,
  CardContent,
  Avatar,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
} from "@mui/lab";
import { timelineItemClasses } from "@mui/lab/TimelineItem";
import Link from "@mui/material/Link";
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "../../../../../store";
import { fetchLeads } from "../../../store/slices/leadSlice";
import type { Contact, Lead } from "../../../types";
import DetailRow from "../../common/DetailRow";
import AddLeadModal from "../Lead/AddLeadModal";

// Local icon placeholders to avoid external icon package dependency
const IconPlaceholder: React.FC<any> = () => null;

const CheckCircleIcon = IconPlaceholder;
const ArrowBackIcon = IconPlaceholder;
const PersonIcon = IconPlaceholder;
const EditIcon = IconPlaceholder;

// Interface for contact with associated leads
interface ContactWithLeads extends Contact {
  leads?: Lead[];
}

export default function ContactDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { contacts } = useAppSelector((state) => state.contacts);
  const { leads, loading: leadsLoading } = useAppSelector((state) => state.leads);

  const [contact, setContact] = useState<ContactWithLeads | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateLeadModalOpen, setIsCreateLeadModalOpen] = useState(false);

  // Fetch leads when component mounts or contact ID changes
  useEffect(() => {
    if (id) {
      dispatch(fetchLeads({ page: 1, limit: 1000 })); // Fetch all leads to filter by contactId
    }
  }, [id, dispatch]);

  useEffect(() => {
    // Set loading state based on leads loading
    if (leadsLoading) {
      setLoading(true);
      return;
    }

    setError(null);

    // Find contact in Redux store
    const foundContact = contacts.find((c) => c.id === id);

    if (foundContact) {
      // Filter leads by contactId (contactId from lead matches the selected contact's ID)
      const contactLeads = leads.filter((lead) => {
        return lead.contactId === id;
      });

      setContact({
        ...foundContact,
        leads: contactLeads,
      });
      setLoading(false);
    } else {
      setError("Contact not found");
      setLoading(false);
    }
  }, [id, contacts, leads, leadsLoading]);

  // Get activity feed items - show leads in decreasing order (newest first)
  const getActivityFeedItems = () => {
    if (!contact || !contact.leads || contact.leads.length === 0) return [];

    // Sort leads by createdAt in decreasing order (newest first)
    const sortedLeads = [...contact.leads].sort((a, b) => {
      const dateA = new Date(a.createdAt || 0).getTime();
      const dateB = new Date(b.createdAt || 0).getTime();
      return dateB - dateA;
    });

    return sortedLeads.map((lead) => ({
      icon: <CheckCircleIcon sx={{ fontSize: 16 }} />,
      color: "primary" as const,
      title: lead.siteName || lead.name || "Lead",
      time: lead.createdAt
        ? new Date(lead.createdAt).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
        : "Date not available",
      lead: lead,
    }));
  };

  // const getLeadStatusBadgeColor = (status: string) => {
  //   switch (status) {
  //     case "new":
  //       return "#3b82f6";
  //     case "contacted":
  //       return "#8b5cf6";
  //     case "qualified":
  //       return "#10b981";
  //     case "converted":
  //       return "#059669";
  //     case "lost":
  //       return "#ef4444";
  //     default:
  //       return "#6b7280";
  //   }
  // };

  // const getLeadInitials = (name: string) => {
  //   return name
  //     .split(" ")
  //     .filter(Boolean)
  //     .map((n) => n[0])
  //     .join("")
  //     .toUpperCase()
  //     .slice(0, 2);
  // };

  const getBreadcrumbs = () => {
    if (contact) {
      return `Home > Contacts > ${contact.name}`;
    }
    return "Home > Contacts > Contact Detail";
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case "active":
        return "#3b82f6";
      case "pending":
        return "#f59e0b";
      case "inactive":
        return "#ef4444";
      default:
        return "#6b7280";
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .filter(Boolean)
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const renderContent = () => {
    if ((loading || leadsLoading) && !contact) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Box>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/", { state: { activeMenu: "contact" } })}
            sx={{ mb: 2 }}
          >
            Back to Contacts
          </Button>
        </Box>
      );
    }

    if (!contact) {
      return (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            Contact not found
          </Alert>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/contacts", { state: { activeMenu: "contact" } })}
            sx={{ mb: 2 }}
          >
            Back to Contacts
          </Button>
        </Box>
      );
    }

    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/contacts", { state: { activeMenu: "contact" } })}
          sx={{ mb: 2 }}
        >
          Back to Contacts
        </Button>

        {/* Header */}
        <Card sx={{ mb: 3, width: "100%", boxShadow: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Grid
              container
              spacing={3}
              alignItems="center"
              flex="1"
              flexDirection="row"
            >
              <Grid size={12}>
                <Stack direction="row" spacing={3} alignItems="center">
                  <Avatar
                    sx={{
                      width: 72,
                      height: 72,
                      bgcolor: "primary.main",
                      fontSize: 28,
                      fontWeight: 600,
                    }}
                  >
                    {getInitials(contact.name)}
                  </Avatar>
                  <Box>
                    <Stack
                      direction="row"
                      spacing={2}
                      alignItems="center"
                      mb={1}
                    >
                      <Typography variant="h4" fontWeight={700} fontSize={28}>
                        {contact.name}
                      </Typography>
                      <Chip
                        label={contact?.status?.toUpperCase()}
                        sx={{
                          backgroundColor: getStatusBadgeColor(contact.status),
                          color: "white",
                          fontWeight: 600,
                          fontSize: 13,
                          height: 28,
                          px: 1,
                        }}
                        size="medium"
                      />
                    </Stack>
                    <Typography color="text.secondary" fontSize={16}>
                      {contact.email && `${contact.email}  ·  ${contact.phone}`}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>

              {/* <Grid size={4} textAlign="right">
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button
                    variant="contained"
                    startIcon={<EditIcon />} // Using EditIcon but it's for Add Lead
                    size="medium"
                    sx={{
                      fontSize: 14,
                      px: 2,
                      background: "linear-gradient(135deg, #1e3a8a 0%, #1e40af 50%, #1d4ed8 100%)",
                      "&:hover": {
                        background: "linear-gradient(135deg, #1e40af 0%, #1d4ed8 50%, #2563eb 100%)",
                      }
                    }}
                    onClick={() => setIsCreateLeadModalOpen(true)}
                  >
                    Create Enquiry
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    size="medium"
                    sx={{ fontSize: 14, px: 2 }}
                    onClick={() => setIsEditModalOpen(true)}
                  >
                    Edit
                  </Button>
                </Stack>
              </Grid> */}
            </Grid>
          </CardContent>
        </Card>

        {/* Main Content */}
        <Grid container spacing={3} justifyContent="space-between">
          {/* Left Section */}
          <Grid size={8}>
            <Stack spacing={3}>
              {/* Combined Contact Information and Status Information */}
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <PersonIcon fontSize="small" color="action" />
                    <Typography fontWeight={600}>
                      Contact Information
                    </Typography>
                  </Box>
                  <Grid container spacing={2} justifyContent="space-between">
                    <Grid size={4}>
                      <DetailRow label="Name" value={contact.name} />
                    </Grid>
                    <Grid size={4}>
                      <DetailRow label="Email" value={contact.email} />
                    </Grid>
                    <Grid size={4}>
                      <DetailRow label="Phone" value={contact.phone} />
                    </Grid>
                    {/* <Grid size={4}>
                      <DetailRow
                        label="Site Name"
                        value={contact.siteName || "Not provided"}
                      />
                    </Grid> */}
                    <Grid size={4}>
                      <DetailRow label="Status" value={contact.status} />
                    </Grid>
                    <Grid size={4}>
                      <DetailRow
                        label="Created"
                        value={
                          contact.createdAt
                            ? new Date(contact.createdAt).toLocaleDateString(
                              "en-US",
                              {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              },
                            )
                            : "Not available"
                        }
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Lead List */}
              <Card>
                <CardContent>
                  <Typography fontWeight={600} mb={2}>
                    Enquiry List
                  </Typography>
                  {contact.leads && contact.leads.length > 0 ? (
                    <Grid container spacing={2}>
                      {contact.leads.map((lead) => (
                        <Grid size={12} key={lead.id}>
                          <Box
                            onClick={() => navigate(`/leads/${lead.id}`)}
                            sx={{
                              cursor: "pointer",
                              padding: 1,
                              "&:hover": {
                                boxShadow: 3,
                                transform: "translateY(-2px)",
                                transition: "all 0.2s ease-in-out",
                              },
                              border: "1px solid #e0e0e0",
                            }}
                          >
                            <Box >
                              {/* Site Name and Number of Units */}
                              <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="flex-start"
                                mb={1}
                              >
                                <Typography
                                  fontSize={16}
                                  fontWeight={600}
                                  sx={{ flex: 1 }}
                                >
                                  {lead.siteName || lead.name || "—"}
                                </Typography>
                                <Typography
                                  fontSize={14}
                                  fontWeight={500}
                                  color="primary"
                                >
                                  {lead.numberOfUnits
                                    ? `${lead.numberOfUnits} Units`
                                    : "—"}
                                </Typography>
                              </Box>

                              {/* Address */}
                              <Typography
                                fontSize={13}
                                color="text.secondary"
                                mb={2}
                              >
                                {(() => {
                                  const address = lead.siteAddress || lead.physicalAddress || "";
                                  const city = lead.siteCity || lead.city || "";
                                  const state = lead.siteState || lead.state || "";
                                  const zip = lead.siteZip || "";

                                  const addressParts = [
                                    address,
                                    city,
                                    state,
                                    zip
                                  ].filter(Boolean);

                                  return addressParts.length > 0
                                    ? addressParts.join(", ")
                                    : "—";
                                })()}
                              </Typography>

                              {/* Last Updated and Created Info */}
                              <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                flexWrap="wrap"
                                gap={2}
                              // sx={{
                              //   pt: 1.5,
                              //   borderTop: "1px solid #f0f0f0",
                              // }}
                              >
                                <Typography
                                  fontSize={13}
                                  color="text.secondary"
                                >
                                  Last updated by{" "}
                                  <Typography
                                    component="span"
                                    fontSize={13}
                                    fontWeight={500}
                                    color="text.primary"
                                  >
                                    {lead.assignedSalesperson ||
                                      lead.salesperson ||
                                      "N/A"}
                                  </Typography>{" "}
                                  ·{" "}
                                  <Typography
                                    component="span"
                                    fontSize={13}
                                    fontWeight={500}
                                    color="text.primary"
                                  >
                                    {lead.updatedAt
                                      ? new Date(
                                        lead.updatedAt,
                                      ).toLocaleDateString("en-US", {
                                        month: "short",
                                        day: "numeric",
                                        year: "numeric",
                                      })
                                      : "N/A"}
                                  </Typography>
                                </Typography>
                                <Typography
                                  fontSize={13}
                                  color="text.secondary"
                                >
                                  Created by{" "}
                                  <Typography
                                    component="span"
                                    fontSize={13}
                                    fontWeight={500}
                                    color="text.primary"
                                  >
                                    {lead.assignedSalesperson ||
                                      lead.salesperson ||
                                      "N/A"}
                                  </Typography>{" "}
                                  ·{" "}
                                  <Typography
                                    component="span"
                                    fontSize={13}
                                    fontWeight={500}
                                    color="text.primary"
                                  >
                                    {lead.createdAt
                                      ? new Date(
                                        lead.createdAt,
                                      ).toLocaleDateString("en-US", {
                                        month: "short",
                                        day: "numeric",
                                        year: "numeric",
                                      })
                                      : "N/A"}
                                  </Typography>
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Typography color="text.secondary" sx={{ py: 2 }}>
                      No leads found for this contact.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Stack>
          </Grid>

          {/* Right Section */}
          <Grid size={4}>
            <Stack spacing={2}>
              {/* Activity Feed */}
              <Card>
                <CardContent>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                  >
                    <Typography fontWeight={600}>Activity Feed</Typography>
                    <Link
                      component="button"
                      variant="body2"
                      sx={{ cursor: "pointer", textDecoration: "none" }}
                      onClick={() => { }}
                    >
                      VIEW ALL
                    </Link>
                  </Box>

                  <Timeline
                    sx={{
                      [`& .${timelineItemClasses.root}:before`]: {
                        flex: 0,
                        padding: 0,
                      },
                    }}
                  >
                    {getActivityFeedItems().length > 0 ? (
                      getActivityFeedItems().map((activity, index, array) => (
                        <TimelineItem key={activity.lead?.id || index}>
                          <TimelineSeparator>
                            <TimelineDot color={activity.color}>
                              {activity.icon}
                            </TimelineDot>
                            {index < array.length - 1 && <TimelineConnector />}
                          </TimelineSeparator>
                          <TimelineContent>
                            <Typography
                              fontSize={14}
                              fontWeight={500}
                              sx={{ cursor: "pointer" }}
                              onClick={() =>
                                activity.lead &&
                                navigate(`/leads/${activity.lead.id}`)
                              }
                            >
                              {activity.title}
                            </Typography>
                            <Typography fontSize={12} color="text.secondary">
                              {activity.time}
                            </Typography>
                          </TimelineContent>
                        </TimelineItem>
                      ))
                    ) : (
                      <Typography
                        fontSize={14}
                        color="text.secondary"
                        sx={{ py: 2 }}
                      >
                        No leads available
                      </Typography>
                    )}
                  </Timeline>
                </CardContent>
              </Card>
            </Stack>
          </Grid>
        </Grid>

        {/* Create Lead Modal (New Enquiry for existing contact) */}
        <AddLeadModal
          isOpen={isCreateLeadModalOpen}
          onRequestClose={() => setIsCreateLeadModalOpen(false)}
          initialData={contact || undefined}
          mode="lead"
        />

        {/* Edit Contact Modal */}
        <AddLeadModal
          isOpen={isEditModalOpen}
          onRequestClose={() => setIsEditModalOpen(false)}
          initialData={contact || undefined}
          mode="contact"
        />
      </Box>
    );
  };

  return (
    <Box
      component="main"
      sx={{
        flex: 1,
        padding: "2rem",
        overflowY: "auto",
        bgcolor: "#f8fafc",
        minHeight: "100vh",
      }}
    >
      {renderContent()}
    </Box>
  );
}
